import React, { useEffect, useMemo, useState } from "react";
import { Show } from "@refinedev/antd";
import { useShow } from "@refinedev/core";
import {
  Typography,
  Space,
  Card,
  Descriptions,
  Tag,
  Table,
  Empty,
  Button,
  message,
  Popconfirm,
  Result,
  Statistic,
  Row,
  Col,
} from "antd";
import {
  CopyOutlined,
  StopOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { supabaseClient } from "../../utility/supabaseClient";

dayjs.extend(relativeTime);

const { Title, Text } = Typography;

const STATUS_META: Record<string, { label: string; color: string }> = {
  active: { label: "可用", color: "green" },
  used: { label: "已用完", color: "default" },
  disabled: { label: "已禁用", color: "red" },
  expired: { label: "已过期", color: "orange" },
};

export const RedemptionCodeShow: React.FC = () => {
  const [userEmails, setUserEmails] = useState<Record<string, string>>({});
  const [usersLoading, setUsersLoading] = useState(false);

  const {
    queryResult: { data, isLoading, isError, error },
  } = useShow({
    meta: {
      select:
        "*, membership_tiers(name, display_name, duration_days), redemption_history(*)",
    },
  });

  const record = data?.data;
  const history = useMemo(() => record?.redemption_history ?? [], [record?.redemption_history]);

  const historyUserIds = useMemo(() => {
    const ids = history
      .map((item: any) => item.user_id)
      .filter((id: string | null | undefined): id is string => Boolean(id));
    return Array.from(new Set(ids)).sort();
  }, [history]);
  const historyUserIdsKey = historyUserIds.join(",");

  useEffect(() => {
    const fetchUserEmails = async () => {
      if (historyUserIds.length === 0) {
        setUserEmails({});
        return;
      }

      setUsersLoading(true);
      try {
        const { data: users, error: usersError } = await supabaseClient.rpc(
          "get_users_by_ids",
          { user_ids: historyUserIds }
        );

        if (usersError) {
          message.error(`加载用户信息失败: ${usersError.message}`);
          return;
        }

        const map = (users || []).reduce(
          (acc: Record<string, string>, user: any) => {
            if (user.id) {
              acc[user.id] = user.email || "未知用户";
            }
            return acc;
          },
          {}
        );
        setUserEmails(map);
      } catch (err: any) {
        message.error(`加载用户信息异常: ${err.message}`);
      } finally {
        setUsersLoading(false);
      }
    };

    fetchUserEmails();
  }, [historyUserIds, historyUserIdsKey]); // 依赖实际列表和稳定 key，避免 warn

  if (isError) {
    return (
      <Show isLoading={isLoading} title="兑换码详情">
        <Result
          status="error"
          title="加载兑换码详情失败"
          subTitle={error?.message || "请稍后重试"}
        />
      </Show>
    );
  }

  if (!record) return null;

  // 检查是否过期
  const isExpired =
    record.valid_until && new Date(record.valid_until) < new Date();
  const displayStatus = isExpired ? "expired" : record.status;
  const statusMeta = STATUS_META[displayStatus];
  const remainingUses =
    typeof record?.max_uses === "number" && typeof record?.current_uses === "number"
      ? Math.max(record.max_uses - record.current_uses, 0)
      : undefined;

  // 禁用兑换码（防御性编程：处理 undefined 情况）
  const disableCode = async () => {
    if (!record?.id) {
      message.error("无效的兑换码ID");
      return;
    }

    try {
      const { error } = await supabaseClient
        .from("redemption_codes")
        .update({
          status: "disabled",
          disabled_at: new Date().toISOString(),
        })
        .eq("id", record.id);

      if (error) {
        message.error(`禁用失败: ${error.message}`);
        return;
      }

      message.success("已禁用该兑换码");
      window.location.reload();
    } catch (err: any) {
      message.error(`禁用异常: ${err.message}`);
    }
  };

  // 复制兑换码
  const copyCode = () => {
    navigator.clipboard.writeText(record.code);
    message.success("已复制到剪贴板");
  };

  return (
    <Show
      isLoading={isLoading}
      title="兑换码详情"
      headerButtons={({ defaultButtons }) => (
        <Space>
          {record.status === "active" && (
            <Popconfirm
              title="确定禁用此兑换码？"
              description="禁用后将无法再被兑换"
              onConfirm={disableCode}
              okText="确定"
              cancelText="取消"
            >
              <Button danger icon={<StopOutlined />}>
                禁用此码
              </Button>
            </Popconfirm>
          )}
          <Button icon={<CopyOutlined />} onClick={copyCode}>
            复制兑换码
          </Button>
          {defaultButtons}
        </Space>
      )}
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {/* 运营摘要 */}
        <Row gutter={[12, 12]}>
          <Col xs={24} md={8}>
            <Card size="small" variant="borderless" bordered>
              <Statistic
                title="状态"
                value={statusMeta.label}
                valueStyle={{ color: statusMeta.color === "green" ? "#52c41a" : "#faad14" }}
              />
              {isExpired && <Text type="danger">已过期</Text>}
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card size="small" variant="borderless" bordered>
              <Statistic
                title="剩余使用次数"
                value={remainingUses ?? "-"}
                suffix={typeof remainingUses === "number" ? "次" : undefined}
              />
              <Text type="secondary">
                已用 {record.current_uses ?? 0} / {record.max_uses ?? "-"}
              </Text>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card size="small" variant="borderless" bordered>
              <Statistic
                title="有效期"
                value={
                  record.valid_until
                    ? dayjs(record.valid_until).format("YYYY-MM-DD HH:mm")
                    : "永久有效"
                }
                valueStyle={record.valid_until && isExpired ? { color: "#ff4d4f" } : undefined}
              />
              {record.valid_until && !isExpired && (
                <Text type="secondary">
                  距离过期 {dayjs(record.valid_until).fromNow(true)}
                </Text>
              )}
            </Card>
          </Col>
        </Row>

        {/* 基本信息 */}
        <Card title="基本信息" size="small" variant="borderless">
          <Descriptions column={2} bordered>
            <Descriptions.Item label="兑换码">
              <Text
                copyable={{ text: record.code, tooltips: ["复制", "已复制"] }}
                code
                style={{ fontSize: 16, fontWeight: "bold" }}
              >
                {record.code}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={statusMeta.color}>{statusMeta.label}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="订阅等级">
              <Tag color="blue">
                {record.membership_tiers?.display_name || "未知"}
              </Tag>
              <Text type="secondary"> ({record.membership_tiers?.duration_days} 天)</Text>
            </Descriptions.Item>
            <Descriptions.Item label="使用情况">
              <Text>
                已使用 <Text strong>{record.current_uses}</Text> /{" "}
                <Text strong>{record.max_uses}</Text> 次
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {dayjs(record.created_at).format("YYYY-MM-DD HH:mm:ss")}
            </Descriptions.Item>
            <Descriptions.Item label="生效时间">
              {dayjs(record.valid_from).format("YYYY-MM-DD HH:mm:ss")}
            </Descriptions.Item>
            <Descriptions.Item label="过期时间">
              {record.valid_until ? (
                <Text type={isExpired ? "danger" : undefined}>
                  {dayjs(record.valid_until).format("YYYY-MM-DD HH:mm:ss")}
                  {isExpired && " (已过期)"}
                </Text>
              ) : (
                <Text type="success">永久有效</Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="禁用时间">
              {record.disabled_at ? (
                dayjs(record.disabled_at).format("YYYY-MM-DD HH:mm:ss")
              ) : (
                <Text type="secondary">-</Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="批次ID" span={2}>
              {record.batch_id || <Text type="secondary">-</Text>}
            </Descriptions.Item>
            <Descriptions.Item label="备注" span={2}>
              {record.notes || <Text type="secondary">-</Text>}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* 兑换历史 */}
        <Card
          title={
            <Space>
              <span>兑换历史</span>
              <Tag>{record.redemption_history?.length || 0} 次兑换</Tag>
            </Space>
          }
          size="small"
          variant="borderless"
        >
          {record.redemption_history && record.redemption_history.length > 0 ? (
            <Table
              dataSource={record.redemption_history}
              rowKey="id"
              pagination={false}
              size="small"
              loading={usersLoading}
            >
              <Table.Column
                title="用户"
                dataIndex="user_id"
                render={(_value, historyRecord: any) => {
                  const email = historyRecord.user_id
                    ? userEmails[historyRecord.user_id]
                    : null;
                  return (
                    <Space direction="vertical" size={0}>
                      <Text>{email || "未知用户"}</Text>
                      {historyRecord.user_id && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          ID: {historyRecord.user_id}
                        </Text>
                      )}
                    </Space>
                  );
                }}
              />
              <Table.Column
                title="兑换时间"
                dataIndex="redeemed_at"
                render={(value: string) =>
                  dayjs(value).format("YYYY-MM-DD HH:mm:ss")
                }
              />
              <Table.Column
                title="授予天数"
                dataIndex="days_granted"
                render={(value?: number) =>
                  typeof value === "number" ? `${value} 天` : <Text type="secondary">-</Text>
                }
              />
              <Table.Column
                title="之前到期"
                dataIndex="previous_expiry"
                render={(value?: string) =>
                  value ? (
                    dayjs(value).format("YYYY-MM-DD HH:mm:ss")
                  ) : (
                    <Text type="secondary">-</Text>
                  )
                }
              />
              <Table.Column
                title="新的到期"
                dataIndex="new_expiry"
                render={(value?: string) =>
                  value ? (
                    dayjs(value).format("YYYY-MM-DD HH:mm:ss")
                  ) : (
                    <Text type="secondary">-</Text>
                  )
                }
              />
              <Table.Column
                title="状态"
                dataIndex="status"
                render={(value: string) => (
                  <Tag
                    color={
                      value === "success"
                        ? "green"
                        : value === "failed"
                        ? "red"
                        : "default"
                    }
                  >
                    {value === "success"
                      ? "成功"
                      : value === "failed"
                      ? "失败"
                      : value === "revoked"
                      ? "已撤销"
                      : value}
                  </Tag>
                )}
              />
              <Table.Column
                title="IP地址"
                dataIndex="ip_address"
                render={(value?: string) => value || <Text type="secondary">-</Text>}
              />
              <Table.Column
                title="设备UA"
                dataIndex="user_agent"
                ellipsis
                render={(value?: string) => value || <Text type="secondary">-</Text>}
              />
            </Table>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="暂无兑换记录"
            />
          )}
        </Card>

        {/* 使用提示 */}
        {record.status === "active" && !isExpired && (
          <Card size="small">
            <Space direction="vertical">
              <Title level={5}>
                <CheckCircleOutlined style={{ color: "#52c41a" }} /> 此兑换码可用
              </Title>
              <Text>
                剩余使用次数：
                <Text strong>{record.max_uses - record.current_uses}</Text> 次
              </Text>
              <Text type="secondary">
                用户可在APP中输入此兑换码激活{" "}
                {record.membership_tiers?.display_name} 会员（{record.membership_tiers?.duration_days} 天）
              </Text>
            </Space>
          </Card>
        )}
      </Space>
    </Show>
  );
};
